import { Button, Input, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React, { useState } from "react";
import ToolPageLayout from "../../components/tools/ToolPageLayout";
import SignUpPromptModal from "../../components/tools/SignUpPromptModal";

const YouTubeSummarizer: React.FC = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [selectedType, setSelectedType] = useState("brief");

  const summaryTypes = [
    { id: "brief", label: "Brief Summary", icon: "lucide:file-text" },
    { id: "detailed", label: "Content Analysis", icon: "lucide:list" },
    { id: "themes", label: "Key Themes", icon: "lucide:lightbulb" },
  ];

  const handleSummarize = () => {
    if (!youtubeUrl.trim()) return;

    setIsLoading(true);
    // Simulate processing then show sign-up modal
    setTimeout(() => {
      setIsLoading(false);
      setShowSignUpModal(true);
    }, 2000);
  };

  const isValidYouTubeUrl = (url: string) => {
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return pattern.test(url);
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "YouTube Video Summarizer - YouWo AI",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Transform long YouTube videos into concise, actionable summaries in seconds. Free AI-powered video summarizer.",
  };

  const howItWorksSteps = [
    {
      title: "Paste YouTube Link",
      description: "Copy and paste any YouTube video URL into the input field above.",
    },
    {
      title: "AI Analyzes Video",
      description: "Our AI extracts the transcript and identifies key points, themes, and insights.",
    },
    {
      title: "Get Your Summary",
      description: "Receive a clear, organized summary with timestamps and key takeaways.",
    },
  ];

  const benefits = [
    {
      title: "Instant Summaries",
      description: "Transform hours of video content into digestible summaries in seconds, not minutes.",
      icon: "lucide:zap",
    },
    {
      title: "Clickable Timestamps",
      description: "Jump to any key moment with clickable timestamps linked to important topics in the video.",
      icon: "lucide:clock",
    },
    {
      title: "100+ Languages",
      description: "Summarize videos in over 100 languages with automatic language detection.",
      icon: "lucide:globe",
    },
    {
      title: "Works with Any Video",
      description: "Lectures, podcasts, tutorials, interviews - summarize any YouTube content.",
      icon: "lucide:video",
    },
    {
      title: "Export with Pro",
      description: "Save summaries as notes, export to PDF, or share with your team with Pro.",
      icon: "lucide:share-2",
    },
    {
      title: "100% Free to Start",
      description: "No credit card required. Start summarizing videos immediately.",
      icon: "lucide:gift",
    },
  ];

  const faqs = [
    {
      question: "What is the YouTube Video Summarizer?",
      answer: "The YouTube Video Summarizer is a free AI-powered tool that transforms long YouTube videos into concise, easy-to-read summaries. It extracts key points, generates timestamps, and helps you understand video content 10x faster.",
    },
    {
      question: "Does it work on videos without captions?",
      answer: "Our AI works best with videos that have captions (either auto-generated or manual). For videos without captions, we use advanced speech recognition to generate transcripts, though accuracy may vary.",
    },
    {
      question: "How accurate are the AI-generated summaries?",
      answer: "Our summaries are highly accurate, capturing the main ideas and key points of videos. The AI is trained on millions of educational videos to understand context, terminology, and important concepts.",
    },
    {
      question: "Is this YouTube summarizer really free?",
      answer: "Yes! The basic YouTube summarizer is completely free. Sign up for a free account to save unlimited summaries and access additional features like AI chat with your videos.",
    },
    {
      question: "Can I export my summaries?",
      answer: "With YouWo AI Pro, you can export summaries to PDF, copy them to your clipboard, or save them directly to your note library for future reference.",
    },
    {
      question: "What types of YouTube videos work best?",
      answer: "Our tool works great for educational content, lectures, podcasts, interviews, tutorials, and any video where extracting information is valuable. It supports videos in 100+ languages.",
    },
  ];

  const relatedTools = [
    {
      name: "PDF Summarizer",
      description: "Turn lengthy PDFs and documents into clear, concise summaries instantly.",
      href: "/tools/pdf-summarizer",
      icon: "lucide:file-text",
      iconColor: "text-orange-500",
    },
    {
      name: "AI Audio Transcriber",
      description: "Convert any audio file to accurate text with speaker detection.",
      href: "/tools/audio-transcriber",
      icon: "lucide:mic",
      iconColor: "text-blue-500",
    },
    {
      name: "AI Flashcard Maker",
      description: "Generate study flashcards automatically from any content.",
      href: "/tools/flashcard-maker",
      icon: "lucide:layers",
      iconColor: "text-purple-500",
    },
    {
      name: "AI Quiz Generator",
      description: "Create practice quizzes to test your understanding of any topic.",
      href: "/tools/quiz-generator",
      icon: "lucide:help-circle",
      iconColor: "text-green-500",
    },
  ];

  return (
    <>
      <ToolPageLayout
        title="Free YouTube Video Summarizer - AI Summary Generator | YouWo AI"
        metaDescription="Transform long YouTube videos into concise summaries in seconds. Free AI-powered YouTube summarizer with timestamps, key points, and instant insights. No sign-up required."
        keywords="youtube summarizer, video summarizer, ai summary, youtube transcript, video notes, lecture notes, free youtube summary tool"
        canonicalUrl="https://youwo.ai/tools/youtube-summarizer"
        structuredData={structuredData}
        heroTitle="YouTube Video Summarizer"
        heroSubtitle="Transform long YouTube videos, podcasts, and lectures into concise summaries with clickable timestamps. Supports 100+ languages. Watch content 10x faster."
        heroIcon="lucide:youtube"
        heroIconColor="text-red-500"
        heroIconBg="bg-red-50"
        howItWorksSteps={howItWorksSteps}
        benefitsTitle="Why Choose YouWo AI YouTube Summarizer?"
        benefits={benefits}
        faqs={faqs}
        relatedTools={relatedTools}
      >
        {/* Tool Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
          {/* URL Input */}
          <div className="flex gap-3 mb-6">
            <div className="flex-1">
              <Input
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                size="lg"
                startContent={
                  <Icon icon="lucide:youtube" className="text-red-500 text-xl" />
                }
                classNames={{
                  input: "text-base",
                  inputWrapper: "h-14 border-gray-200 hover:border-cornflowerblue focus-within:border-cornflowerblue",
                }}
              />
            </div>
            <Button
              color="primary"
              size="lg"
              className="bg-cornflowerblue h-14 px-8 font-semibold"
              isLoading={isLoading}
              isDisabled={!youtubeUrl.trim() || !isValidYouTubeUrl(youtubeUrl.trim())}
              onClick={handleSummarize}
            >
              {isLoading ? "Summarizing..." : "Summarize"}
            </Button>
          </div>

          {/* Summary Type Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <span className="text-sm text-gray-600 font-medium">Summary Type:</span>
            <div className="flex flex-wrap gap-2">
              {summaryTypes.map((type) => (
                <Chip
                  key={type.id}
                  variant={selectedType === type.id ? "solid" : "bordered"}
                  color={selectedType === type.id ? "primary" : "default"}
                  className={`cursor-pointer transition-all ${
                    selectedType === type.id
                      ? "bg-cornflowerblue text-white"
                      : "border-gray-200 text-gray-600 hover:border-cornflowerblue"
                  }`}
                  startContent={<Icon icon={type.icon} className="text-sm" />}
                  onClick={() => setSelectedType(type.id)}
                >
                  {type.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Demo Preview */}
          {youtubeUrl && isValidYouTubeUrl(youtubeUrl) && !isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100"
            >
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon icon="lucide:info" />
                <span>Ready to summarize. Click "Summarize" to generate your AI summary.</span>
              </div>
            </motion.div>
          )}
        </div>
      </ToolPageLayout>

      <SignUpPromptModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        toolName="YouTube Summary"
      />
    </>
  );
};

export default YouTubeSummarizer;
